/**
 * Care Self - Shopify Theme JavaScript
 * Native Vanilla JS without external dependencies
 */

class CareSelfTheme {
  constructor() {
    const inits = [
      'initAccordions', 'initGallery', 'initSupplySelector', 'initQuantitySteppers',
      'initCartDrawer', 'initMobileNav', 'initStickyAtc', 'initBuyNow',
      'initReviewPhotoLightbox'
    ];
    inits.forEach((name) => {
      try {
        this[name]();
      } catch (err) {
        console.error(`CareSelfTheme.${name} failed to initialize:`, err);
      }
    });
    this.initThemeEditorEvents();
    this.initBfcacheReload();
  }

  initBfcacheReload() {
    window.addEventListener('pageshow', (e) => {
      if (e.persisted) location.reload();
    });
  }

  initReviewPhotoLightbox() {
    const lightbox = document.querySelector('[data-review-photo-lightbox]');
    if (!lightbox) return;
    const img = lightbox.querySelector('[data-review-photo-lightbox-img]');

    document.querySelectorAll('[data-review-photo]').forEach(btn => {
      btn.addEventListener('click', () => {
        img.src = btn.dataset.reviewPhoto;
        img.alt = btn.querySelector('img')?.alt || '';
        lightbox.hidden = false;
      });
    });

    const close = () => { lightbox.hidden = true; img.src = ''; };
    lightbox.querySelector('[data-review-photo-close]')?.addEventListener('click', close);
    lightbox.addEventListener('click', (e) => {
      if (e.target === lightbox) close();
    });
  }

  initAccordions() {
    document.querySelectorAll('.accordion-trigger').forEach(trigger => {
      trigger.addEventListener('click', (e) => {
        const item = trigger.closest('.accordion-item');
        const isOpen = item.classList.contains('is-open');

        const group = item.closest('.accordion-group');
        if (group && group.dataset.singleOpen === 'true') {
          group.querySelectorAll('.accordion-item').forEach(i => i.classList.remove('is-open'));
        }

        if (isOpen) {
          item.classList.remove('is-open');
          trigger.setAttribute('aria-expanded', 'false');
        } else {
          item.classList.add('is-open');
          trigger.setAttribute('aria-expanded', 'true');
        }
      });
    });
  }

  initGallery() {
    const mainImg = document.querySelector('.product-gallery__main img');
    const thumbs = document.querySelectorAll('.product-gallery__thumb');

    if (!mainImg || thumbs.length === 0) return;

    thumbs.forEach(thumb => {
      thumb.addEventListener('click', () => {
        const targetSrc = thumb.dataset.targetSrc;
        const targetAlt = thumb.dataset.targetAlt;
        if (targetSrc) {
          mainImg.src = targetSrc;
          if (targetAlt) mainImg.alt = targetAlt;
        }
        thumbs.forEach(t => t.classList.remove('is-active'));
        thumb.classList.add('is-active');
      });
    });
  }

  /* Custom Supply Bundle Selector — bound to real Shopify variants */
  initSupplySelector() {
    const cards = document.querySelectorAll('.supply-option-card');
    const variantInput = document.querySelector('[data-variant-input]');
    const priceEl = document.querySelector('[data-current-price]');
    const perStripEl = document.querySelector('[data-current-per-strip]');
    const stickyPriceEl = document.querySelector('[data-sticky-price]');
    const mainImg = document.querySelector('.product-gallery__main img');
    const thumbs = document.querySelectorAll('.product-gallery__thumb');

    if (cards.length === 0) return;

    const syncButtonText = (label) => {
      const btnTextEl = document.querySelector('.btn-add-to-bag .btn-text');
      if (btnTextEl) btnTextEl.textContent = label;
    };
    this.syncButtonTextFromSelectedCard = () => {
      const selected = document.querySelector('.supply-option-card.is-selected');
      if (selected) syncButtonText(`Add to Cart — ${selected.dataset.variantPrice}`);
    };

    const selectCard = (card) => {
      cards.forEach(c => {
        c.classList.remove('is-selected');
        c.setAttribute('aria-checked', 'false');
      });
      card.classList.add('is-selected');
      card.setAttribute('aria-checked', 'true');

      const variantId = card.dataset.variantId;
      const variantPrice = card.dataset.variantPrice;
      const perStripPrice = card.dataset.perStripPrice;
      const variantImage = card.dataset.variantImage;
      const variantImageAlt = card.dataset.variantImageAlt;

      if (variantInput && variantId) {
        variantInput.value = variantId;
        variantInput.dispatchEvent(new Event('change', { bubbles: true }));
      }

      if (variantPrice) syncButtonText(`Add to Cart — ${variantPrice}`);
      if (priceEl && variantPrice) priceEl.textContent = variantPrice;
      if (perStripEl && perStripPrice) perStripEl.textContent = `${perStripPrice} per strip`;
      if (stickyPriceEl && variantPrice) stickyPriceEl.textContent = variantPrice;

      if (mainImg && variantImage) {
        mainImg.src = variantImage;
        if (variantImageAlt) mainImg.alt = variantImageAlt;

        thumbs.forEach(t => {
          t.classList.toggle('is-active', t.dataset.targetSrc === variantImage);
        });
      }
    };

    cards.forEach(card => {
      card.addEventListener('click', () => selectCard(card));
      card.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          selectCard(card);
        }
      });
    });
  }

  initQuantitySteppers() {
    document.querySelectorAll('.quantity-stepper').forEach(stepper => {
      const input = stepper.querySelector('.quantity-input');
      const btnMinus = stepper.querySelector('.quantity-btn--minus');
      const btnPlus = stepper.querySelector('.quantity-btn--plus');

      if (!input) return;

      if (btnMinus) {
        btnMinus.addEventListener('click', () => {
          const currentVal = parseInt(input.value, 10) || 1;
          if (currentVal > 1) {
            input.value = currentVal - 1;
            input.dispatchEvent(new Event('change', { bubbles: true }));
          }
        });
      }

      if (btnPlus) {
        btnPlus.addEventListener('click', () => {
          const currentVal = parseInt(input.value, 10) || 1;
          input.value = currentVal + 1;
          input.dispatchEvent(new Event('change', { bubbles: true }));
        });
      }
    });
  }

  initBuyNow() {
    const btn = document.querySelector('[data-buy-now]');
    if (!btn) return;

    btn.addEventListener('click', async () => {
      if (this._buyNowInFlight) return;
      this._buyNowInFlight = true;

      const form = btn.closest('form') || document.getElementById('product-form-main');
      if (!form) { this._buyNowInFlight = false; return; }

      const variantInput = form.querySelector('[data-variant-input]');
      const quantityInput = form.querySelector('.quantity-input');
      const id = variantInput ? variantInput.value : null;
      const quantity = quantityInput ? (parseInt(quantityInput.value, 10) || 1) : 1;

      if (!id) { this._buyNowInFlight = false; return; }

      const originalText = btn.textContent;
      btn.disabled = true;
      btn.textContent = 'Redirecting…';

      try {
        const res = await fetch('/cart/add.js', {
          method: 'POST',
          cache: 'no-store',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ items: [{ id, quantity }] })
        });
        if (res.ok) {
          window.location.href = '/checkout';
          return;
        }
        const result = await res.json().catch(() => null);
        console.warn('Buy Now: cart add rejected:', result);
      } catch (err) {
        console.warn('Buy Now error:', err);
      }

      btn.disabled = false;
      btn.textContent = originalText;
      this._buyNowInFlight = false;
    });
  }

  initStickyAtc() {
    const bar = document.querySelector('[data-sticky-atc]');
    const trigger = document.querySelector('.product-form-actions');
    if (!bar || !trigger || !('IntersectionObserver' in window)) return;

    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        bar.classList.toggle('is-visible', !entry.isIntersecting);
      });
    }, { rootMargin: '-80px 0px 0px 0px' });

    observer.observe(trigger);
  }

  /* Cart Drawer & AJAX Actions */
  initCartDrawer() {
    const drawer = document.querySelector('.cart-drawer');
    const overlay = document.querySelector('.cart-drawer-overlay');
    const openBtns = document.querySelectorAll('[data-open-cart-drawer]');

    if (!drawer) return;

    const openDrawer = () => {
      drawer.classList.add('is-open');
      if (overlay) overlay.classList.add('is-active');
      document.body.style.overflow = 'hidden';
    };

    const closeDrawer = () => {
      drawer.classList.remove('is-open');
      if (overlay) overlay.classList.remove('is-active');
      document.body.style.overflow = '';
    };
    this.closeCartDrawer = closeDrawer;

    openBtns.forEach(btn => btn.addEventListener('click', (e) => {
      e.preventDefault();
      openDrawer();
      this.refreshCartDrawer();
    }));

    if (overlay) {
      overlay.addEventListener('click', closeDrawer);
    }

    document.addEventListener('click', async (e) => {
      if (e.target.closest('[data-close-cart-drawer]')) {
        e.preventDefault();
        closeDrawer();
        return;
      }

      const removeLink = e.target.closest('#CartDrawer .cart-item__remove');
      if (removeLink) {
        e.preventDefault();
        if (removeLink.dataset.busy === 'true') return;
        removeLink.dataset.busy = 'true';
        const url = new URL(removeLink.href, window.location.origin);
        const line = url.searchParams.get('line');
        try {
          await fetch('/cart/change.js', {
            method: 'POST',
            cache: 'no-store',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ line: Number(line), quantity: 0 })
          });
          await this.refreshCartDrawer();
        } finally {
          removeLink.dataset.busy = 'false';
        }
      }
    });

    document.querySelectorAll('form[action*="/cart/add"]').forEach(form => {
      form.addEventListener('submit', async (e) => {
        e.preventDefault();

        if (this._addToCartInFlight) return;
        this._addToCartInFlight = true;

        const submitBtn = form.querySelector('[type="submit"].btn-add-to-bag');
        const btnTextEl = submitBtn ? submitBtn.querySelector('.btn-text') : null;

        if (submitBtn) submitBtn.disabled = true;
        if (btnTextEl) btnTextEl.textContent = 'Adding...';

        const formData = new FormData(form);

        try {
          const response = await fetch('/cart/add.js', {
            method: 'POST',
            cache: 'no-store',
            body: formData,
            headers: {
              'X-Requested-With': 'XMLHttpRequest'
            }
          });

          const result = await response.json().catch(() => null);

          if (response.ok && result && !result.status) {
            openDrawer();
            await this.refreshCartDrawer();
          } else {
            console.warn('Cart add rejected by Shopify:', result);
            if (btnTextEl) btnTextEl.textContent = (result && result.description) || 'Could not add — try again';
          }
        } catch (err) {
          console.warn('AJAX cart add error:', err);
          if (btnTextEl) btnTextEl.textContent = 'Could not add — try again';
        } finally {
          if (submitBtn) submitBtn.disabled = false;
          this._addToCartInFlight = false;
          if (this.syncButtonTextFromSelectedCard) this.syncButtonTextFromSelectedCard();
        }
      });
    });
  }

  async refreshCartDrawer() {
    const requestId = (this._cartRefreshSeq = (this._cartRefreshSeq || 0) + 1);
    try {
      const sectionRes = await fetch(`/?section_id=cart-drawer&_ts=${Date.now()}`, { cache: 'no-store' });

      if (requestId !== this._cartRefreshSeq) return;

      if (!sectionRes.ok) return;
      const html = await sectionRes.text();
      if (requestId !== this._cartRefreshSeq) return;

      const temp = document.createElement('div');
      temp.innerHTML = html;
      const freshDrawer = temp.querySelector('#CartDrawer');
      const liveDrawer = document.querySelector('#CartDrawer');
      if (!freshDrawer || !liveDrawer) return;

      liveDrawer.innerHTML = freshDrawer.innerHTML;
      const itemCount = Number(freshDrawer.dataset.cartItemCount || 0);
      liveDrawer.dataset.cartItemCount = String(itemCount);

      document.querySelectorAll('.cart-count-badge').forEach(badge => {
        badge.textContent = itemCount;
        badge.style.display = itemCount > 0 ? 'flex' : 'none';
      });
    } catch (e) {
      console.warn('Cart drawer refresh failed:', e);
    }
  }

  initMobileNav() {
    const toggle = document.querySelector('.mobile-nav-toggle');
    const mobileMenu = document.querySelector('.mobile-menu-drawer');
    const closeBtn = document.querySelector('.mobile-menu-close');

    if (!toggle || !mobileMenu) return;

    toggle.addEventListener('click', () => {
      mobileMenu.classList.add('is-open');
      document.body.style.overflow = 'hidden';
    });

    if (closeBtn) {
      closeBtn.addEventListener('click', () => {
        mobileMenu.classList.remove('is-open');
        document.body.style.overflow = '';
      });
    }
  }

  initThemeEditorEvents() {
    document.addEventListener('shopify:section:load', () => {
      this.initAccordions();
      this.initGallery();
      this.initSupplySelector();
      this.initQuantitySteppers();
      this.initStickyAtc();
      this.initBuyNow();
    });
  }
}

document.addEventListener('DOMContentLoaded', () => {
  window.careSelfTheme = new CareSelfTheme();
});
